import _ from "underscore";

import api from "@/helpers/api";
import bus from "@/helpers/bus";
import { extractMessageLocation } from "@/helpers/location";
import logger from "@/helpers/logger";
import Storage from "@/helpers/storage";

export default {
  state: {
    all: [],
    conversationId: null,
    queue: [],
    processingQueue: false,
    formFields: [],
    formData: [],
    queueDelay: 0
  },

  mutations: {
    setMessagesConversationId(state, id) {
      state.conversationId = id;
    },

    appendMessage(state, message) {
      const exists = _.find(state.all, (m) => {
        return m.id === message.id;
      });

      if (exists) return;

      const metadata = message?.attributes?.metadata || {};
      const location = extractMessageLocation(message);
      if (location) {
        message.location = location;
      }

      state.all.push(message);
      state.formFields = metadata.form_fields || [];
      state.formData = metadata.form_data || {};

      state.queue = _.reject(state.queue, (msg) => {
        return msg.id === message.id;
      });
    },

    queueMessage(state, message) {
      state.queue.push(message);
    },

    setProcessingQueue(state, status) {
      state.processingQueue = status;
    },

    clearFormFields(state) {
      state.formFields = [];
    },

    setQueueDelay(state, delay) {
      state.queueDelay = delay;
    },

    updateMessage(state, message) {
      state.all = _.map(state.all, (msg) => {
        return msg.id === message.id ? message : msg;
      });
    },

    clearMessages(state) {
      state.all = [];
      state.conversationId = null;
      Storage.removeItem("conversationMessages");
    },

    restoreMessagesFromStorage(state) {
      const stored = Storage.getItem("conversationMessages");
      if (stored) {
        try {
          state.all = JSON.parse(stored);
        } catch (e) {
          logger.info("Failed to restore messages from storage", e);
          state.all = [];
        }
      }
    }
  },

  actions: {
    setMessagesConversationId({ commit }, id) {
      commit("setMessagesConversationId", id);
    },

    addMessage({ state, commit, dispatch }, opts) {
      // skip if message already exists
      if (_.findWhere(state.all, { id: opts.message.id })) return;

      // queue the message to be appended
      if (opts.queue) {
        commit("queueMessage", opts.message);
        if (!state.processingQueue) dispatch("processQueue");

        // instantly append message
      } else {
        commit("appendMessage", opts.message);
      }
    },

    processQueue({ state, rootState, commit, dispatch }) {
      // Queue is empty - finish processing
      if (state.queue.length === 0) {
        commit("setProcessingQueue", false);
        return;
      }

      commit("setProcessingQueue", true);
      const msg = state.queue[0];
      const content = typeof msg?.attributes?.content === "string" ? msg.attributes.content : "";
      const msgLen = content.length;
      const writeDelay = parseInt(rootState.settings.settings.write_delay_per_char, 10) || 0;

      setTimeout(() => {
        bus.$emit("replyDots:show", true);
        setTimeout(() => {
          bus.$emit("replyDots:show", false);
          commit("appendMessage", msg);
          dispatch("processQueue");
        }, msgLen * writeDelay);

        commit("setQueueDelay", 0);
      }, state.queueDelay);
    },

    createMessage({ state, rootState, commit }, params) {
      bus.$emit("connectPusher");

      params.conversation_id = state.conversationId;

      return api
        .instance()
        .post("/public/v1/messages", params)
        .then((resp) => {
          commit("appendMessage", resp.data.data);

          const content = typeof resp?.data?.data?.attributes?.content === "string" ? resp.data.data.attributes.content : "";
          const msgLen = content.length;
          const readDelay = parseInt(rootState.settings.settings.read_delay_per_char, 10) || 0;

          commit("setQueueDelay", msgLen * readDelay);
          return resp.data.data;
        });
    },

    skipForm({ state, commit }) {
      return api
        .instance()
        .post("/public/v1/messages/skip", { conversation_id: state.conversationId })
        .then((resp) => {
          logger.info("skipped question", resp);
          commit("clearFormFields");
          commit("appendMessage", resp.data.data);
          return resp.data.data;
        })
        .catch((err) => {
          console.error("error skipping question", err);
          throw err;
        });
    },

    sendInitialMessage({ state, rootState, commit }) {
      logger.info("Send intitial message");
      const delay = parseInt(rootState.settings.settings.initial_message_timeout, 10) * 1000;

      setTimeout(() => {
        if (state.all.length === 0) {
          api
            .instance()
            .get("/public/v1/messages/initial_message", {
              params: {
                conversation_id: state.conversationId
              }
            })
            .then((resp) => {
              if (resp.status !== 200) return;
              const msg = resp.data.data;
              msg.isInitialMessage = true;

              commit("appendMessage", msg);
            })
            .catch((err) => {
              logger.info("Error fetching initial message", err);
            });
        }
      }, delay);
    },

    messageRead({ commit }, id) {
      return api
        .instance()
        .put("/public/v1/messages/" + id + "/read")
        .then((resp) => {
          commit("updateMessage", resp.data.data);
          return resp.data.data;
        })
        .catch((err) => {
          console.error("error marking message as read", err);
          throw err;
        });
    },

    restoreFromStorage({ commit }) {
      commit("restoreMessagesFromStorage");
    },

    reset({ commit }) {
      commit("clearMessages");
    }
  }
};
